import { Details, useDetails } from '@primer/react';
import {
  useCallback, useEffect, useRef, useState
} from 'react';

import formatRFC3339 from 'date-fns/formatRFC3339';
import fromUnixTime from 'date-fns/fromUnixTime';
import range from 'lodash/range'
import round from 'lodash/round'

import usePreferredTimeRange from '../contexts/TimeRangeContext/TimeRangeContext';

import { AssayConfig } from './AssayConfig';
import BasicDropdown from '../common/BasicDropdown/BasicDropdown';
import Label from '../common/Labels/Label';
import ModalAssayChart from './ModalAssayChart/ModalAssayChart';
import TextInputWithError from '../common/TextInputs/TextInputWithError/TextInputWithError';
import TimeRangePicker from '../TimeRangePicker/TimeRangePicker';

import keycloakService from '../../keycloak_service';
import { sinatraOrigin } from '../../config';

import { doesArrayHaveNullValues } from '../../utils/product_utils';
import { mapOnlyAssayResults } from '../../utils/assay_utils';

import AssayCustomBinningTable from './AssayCustomBinningTable/AssayCustomBinningTable';

// import usePipelineData from '../pipelines/PipelineDataContext'
import useAssayLoadingContext from './AssayLoadingContext';

// model-insights::assay::mod.rs:102
const SchedulingOptions = [
  { id: 'day', label: 'Daily' },
  { id: 'minute', label: 'Every Minute' },
  { id: 'hour', label: 'Hourly' },
  { id: 'week', label: 'Weekly' }
]

const GroupingOptions = [
  { id: 'day', label: 'Daily' },
  { id: 'week', label: 'Weekly' },
  { id: 'month', label: 'Monthly' }
]

// Number of Bins
const AggregationBinOptions = range(5, 15, 1).map(bin => ({ id: bin, label: `${bin} Bins` }));

const AggregationTypeOptions = [
  { id: 'Density', label: 'Density' },
  { id: 'Cumulative', label: 'Cumulative' },
]

const MetricOptions = [
  { id: 'PSI', label: 'Population Stability Index' },
  { id: 'MaxDiff', label: 'Maximum Difference of Bins' },
  { id: 'SumDiff', label: 'Sum of the Difference of Bins' },
]

const BinOptions = [
  { id: 'Equal', label: 'Equally Spaced' },
  { id: 'Quantile', label: 'Quantile' },
  { id: 'Provided', label: 'Custom' }
]

const BinWeightsOptions = [
  { id: 'Equal', label: 'Equally Weighted' },
  { id: 'Provided', label: 'Custom' }
]

const ThresholdOptions = range(0.1, 0.95, 0.05).map((x) => {
  const val = round(x, 2);
  return { id: val, label: val };
});

const SchedulingTime = range(1, 24).map((h) => {
  let label = '12:00 AM';
  if (h - 12 > 0) {
    label = `${h - 12}:00 PM`;
  } else if (h === 12) {
    label = '12:00 AM'
  } else {
    label = `${h}:00 AM`;
  }

  return { id: h, label }
})

const ChartSection = ({
  assayConfig,
  startTimestamp,
  endTimestamp,
  setError,
}) => {
  const postToApi = (sinatraEndpoint = '', fetchOptions = {}) => fetch(`${sinatraOrigin}/${sinatraEndpoint}`, fetchOptions).then(response => response.json())

  const [results, setResults] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);

  const { assayPreviewLoading, setAssayPreviewLoading } = useAssayLoadingContext();
  
  useEffect(() => {
    (async () => {
      // Prevent flickering due to all data loading if no start is specified.
      if (!assayConfig.window.start) {
        return;
      }

      // Prevent errors from incomplete edges and bin weight arrays
      if (assayConfig.summarizer.provided_edges?.length && doesArrayHaveNullValues(assayConfig.summarizer.provided_edges)) {
        return;
      }
      if (assayConfig.summarizer.bin_weights?.length && doesArrayHaveNullValues(assayConfig.summarizer.bin_weights)) {
        return;
      }

      // We are intentionally setting the start date to be null in the config so that
      // MI calculates all assay scores beyond the preview range.
      const formattedStartDate = formatRFC3339(fromUnixTime(startTimestamp));
      const formattedEndDate = formatRFC3339(fromUnixTime(endTimestamp));

      const modifiedConfig = { ...assayConfig };
      modifiedConfig.window.start = formattedStartDate;
      modifiedConfig.run_until = formattedEndDate;
      modifiedConfig.iopath = assayConfig.getIOPath();

      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: keycloakService.getAuthHeader(),
        },
        body: JSON.stringify(modifiedConfig)
      };

      setAssayPreviewLoading(true)
      try {
        const ret = await postToApi('v1/api/assays/run_interactive', fetchOptions);
        if (!ret?.length) {
          setError('No results in selected time range.')
        } else {
          setError(null);
          setResults(ret);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load results for selected options.')
      }
      setAssayPreviewLoading(false)
    })();
  }, [assayConfig, endTimestamp, setError, startTimestamp, setAssayPreviewLoading]);

  return (
    <ModalAssayChart
      assayName="Preview"
      assayDetails={assayConfig}
      assayRunResults={results ?? []}
      setSelectedRun={setSelectedRun}
      selectedRun={selectedRun ? mapOnlyAssayResults(selectedRun) : null}
      isLoading={assayPreviewLoading}
      hideNormalDropdownOptions
      showIconMenu
      loadingText="Generating preview..."
    />
  )
}

const AdvancedSettingsMenu = ({
  assayConfig,
  setAssayConfig,
  setError,
  startTimestamp,
  endTimestamp,
}) => {
  const [binWeightsFlag, setBinWeightsFlag] = useState(BinWeightsOptions[0].id);

  const [advancedSettings, setAdvancedSettings] = useState({
    run_freq: SchedulingOptions[0].id,
    grouping_freq: GroupingOptions[0].id,
    next_run_time: SchedulingTime[0].id,
    aggType: assayConfig.summarizer.aggregation ?? AggregationTypeOptions[0].id,
    numBins: assayConfig.summarizer.num_bins ?? AggregationBinOptions[0].id,
    threshold: assayConfig.alert_threshold ?? ThresholdOptions[0].id,
    metric: assayConfig.summarizer.metric ?? MetricOptions[0].id,
    binMode: assayConfig.summarizer.bin_mode ?? BinOptions[2].id,
    binWeights: assayConfig.summarizer.bin_weights ?? null,
    edges: assayConfig.summarizer.provided_edges ?? null,
  });

  const {
    numBins: numberOfBins,
    binMode,
  } = advancedSettings;
  const useCustomBinningMode = binMode === BinOptions[2].id;

  useEffect(() => {
    // Clear error on change.
    setError(null);
    setAssayConfig(conf => new AssayConfig(conf.setAdvancedSettings(advancedSettings, startTimestamp, endTimestamp)));
  }, [advancedSettings, endTimestamp, setAssayConfig, setError, startTimestamp]);

  const setEdges = useCallback((edges) => {
    setAdvancedSettings(settings => ({ ...settings, edges }))
  }, []);

  const setWeights = useCallback((binWeights) => {
    setAdvancedSettings(settings => ({ ...settings, binWeights }))
  }, []);

  return (
    <>
      <div className="flex items-start justify-start p-2 mx-2">
        <div className="flex flex-col items-start justify-start w-1/2">
          <Label bold>Scheduling</Label>
          <div className="flex items-center relative">
            <BasicDropdown
              popoverClass="h-40 overflow-scroll"
              direction="top-left"
              dropdownLabel={null}
              buttonLabelSize="md"
              iconFill="brand"
              iconSize={12}
              // We don't need to hydrate this from a config, so local state is okay.
              selectedValue={advancedSettings.run_freq}
              isDisabled={false}
              values={SchedulingOptions}
              onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, run_freq: e }))}
              hideBorder
            />
            at
            <BasicDropdown
              popoverClass="h-40 overflow-scroll"
              direction="top-left"
              dropdownLabel={null}
              buttonLabelSize="md"
              iconFill="brand"
              iconSize={12}
              // icon={KebabHorizontalIcon}
              isDisabled={false}
              // We don't need to hydrate this from a config, so local state is okay.
              selectedValue={advancedSettings.next_run_time}
              values={SchedulingTime}
              onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, next_run_time: e }))}
              hideBorder
            />
          </div>
        </div>
        <div className="flex flex-col items-start justify-start w-1/2">
          <Label bold>Group Results</Label>
          <div className="flex items-center relative">
            <BasicDropdown
              popoverClass="h-40 overflow-scroll"
              direction="top-left"
              dropdownLabel={null}
              buttonLabelSize="md"
              iconFill="brand"
              iconSize={12}
              // We don't need to hydrate this from a config, so local state is okay.
              selectedValue={advancedSettings.grouping_freq}
              isDisabled={false}
              values={GroupingOptions}
              onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, grouping_freq: e }))}
              hideBorder
            />
          </div>
        </div>
      </div>
      <div className="flex items-start justify-between relative p-2 mx-2">
        <div className="flex flex-col items-start justify-center w-1/3">
          <Label bold>Aggregation Type</Label>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.aggType}
            isDisabled={false}
            values={AggregationTypeOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, aggType: e }))}
            hideBorder
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <Label bold>Threshold</Label>
          <TextInputWithError
            type="number"
            step={0.06}
            // Fixes number change on scrolling up or down on input
            onWheel={e => e.target.blur()}
            errorMessage="Please enter a numeric value."
            onChange={e => setAdvancedSettings(settings => ({ ...settings, threshold: e.toString() }))}
            value={advancedSettings.threshold}
            wrapperStyle={{ marginTop: '8px' }}
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <Label bold>Metric</Label>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.metric}
            isDisabled={false}
            values={MetricOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, metric: e }))}
            hideBorder
          />
        </div>
      </div>
      <div className="flex items-start justify-between relative p-2 mx-2">
        <div className="flex flex-col items-start justify-center w-1/3">
          <Label bold>Number of Bins</Label>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.numBins}
            isDisabled={false}
            values={AggregationBinOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, numBins: e }))}
            hideBorder
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <Label bold>Bin Mode</Label>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={advancedSettings.binMode}
            isDisabled={false}
            values={BinOptions}
            onValueSelected={e => setAdvancedSettings(settings => ({ ...settings, binMode: e }))}
            hideBorder
          />
        </div>
        <div className="flex flex-col items-start justify-center w-1/3">
          <Label bold>Bin Weights</Label>
          <BasicDropdown
            popoverClass="h-40 overflow-scroll"
            direction="top-left"
            dropdownLabel={null}
            buttonLabelSize="md"
            iconFill="brand"
            iconSize={12}
            selectedValue={binWeightsFlag}
            isDisabled={false}
            values={BinWeightsOptions}
            onValueSelected={e => setBinWeightsFlag(e)}
            hideBorder
          />
        </div>
      </div>
      <div className="flex items-start relative p-2 mx-2">
        <AssayCustomBinningTable
          numberOfBins={numberOfBins}
          setEdges={setEdges}
          setWeights={setWeights}
          useCustomBinEdges={useCustomBinningMode}
          useCustomWeights={binWeightsFlag === BinWeightsOptions[1].id}
        />
      </div>
    </>
  )
}

export const PreviewHistorical = ({
  assayConfig,
  setAssayConfig,
  setError
}) => {
  const ref = useRef(null);
  const {
    getDetailsProps, open, onToggle
  } = useDetails({ closeOnOutsideClick: false, ref })

  const {
    startTimestamp,
    endTimestamp,
    selectedTimeRange,
    onTimeRangeChange,
  } = usePreferredTimeRange()
  
  return <div className="flex flex-col h-full">
    <div className="mb-2">
      <TimeRangePicker selectedRange={selectedTimeRange} defaultRange={selectedTimeRange} onChange={onTimeRangeChange} />
    </div>
    <ChartSection
      assayConfig={assayConfig}
      startTimestamp={startTimestamp}
      endTimestamp={endTimestamp}
      setError={setError}
    />
    <Details
      {...getDetailsProps()}
      onToggle={(...x) => {
        if (x === ref) {
          onToggle();
        }
      }}
    >
      <summary className="flex cursor-pointer" style={{ padding: '10px 0px' }}><b>Advanced Settings {open ? <>&#9650;</> : <>&#9660;</>}</b></summary>
      <AdvancedSettingsMenu
        startTimestamp={startTimestamp}
        endTimestamp={endTimestamp}
        assayConfig={assayConfig}
        setAssayConfig={setAssayConfig}
        setError={setError}
      />
    </Details>
  </div>;
};

export default PreviewHistorical;
